<template>
  <!-- Disable the <div> when the deactivate condition is true, 
    which is determined by whether the user has selected the required details or not 
  -->
  <div :class="{ [$style.disabled]: deactivate }">
    <!-- Select ProjectType container field -->
    <div :class="$style.radioBtnContainer">
      <label class="label">Project Type</label>
      <b-field grouped class="margin-top6 columns">
        <!-- Select Project Type Options -->
        <b-radio-button
          expanded
          v-for="option in RecurrenceType"
          v-model="selectedRecurrenceType"
          :key="option"
          :native-value="option"
          type="is-primary"
          class="column is-3 radio-option-style-first"
          :class="
            selectedRecurrenceType === option
              ? $style.activeRadioBtn
              : $style.radioBtn
          "
        >
          <span
            :class="
              selectedRecurrenceType === option
                ? $style.activeHeader
                : $style.header
            "
          >
            <div class="audit-flex-display">
              <p class="audit-type-text-1 text-size-body">
                <b>{{ recurrenceTypeDisplayInfo(option).title }}</b>
              </p>
              <p class="audit-type-text-2 text-size">
                {{ recurrenceTypeDisplayInfo(option).infotext1 }}
              </p>
              <!-- Added v-if to conditionally render 'infotext2' since it is optional -->
              <p
                class="audit-type-text-3 text-size"
                v-if="recurrenceTypeDisplayInfo(option).infotext2"
              >
                {{ recurrenceTypeDisplayInfo(option).infotext2 }}
              </p>
            </div>
          </span>
        </b-radio-button>
      </b-field>
    </div>

    <!-- Date Picker -->
    <div class="columns is-multiline">
      <!-- Select Start Date -->
      <div class="column is-half margin-bottom6">
        <div class="field" :class="$style.period">
          <label class="label">
            Start Date
            <!-- Add 'Optional' label only when the recurrenceType is not 'MULTIPLE' -->
            <span
              v-if="
                recurrenceSetting.recurrenceType !== RecurrenceType.MULTIPLE
              "
            >
              (Optional)
            </span>
          </label>
          <div class="columns">
            <div class="column">
              <b-datepicker
                placeholder="Select a start date"
                icon="calendar-today"
                icon-pack="mdi"
                v-model="scheduledRecurrenceStartDate"
                @input="updateTotalDataCollectionPeriod"
                :class="[$style.customCalendarIcon, 'calender-icon']"
                :date-formatter="(date) => formatDateRange(date)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Select Sampling Type -->
      <div class="column is-half padding-left4">
        <b-field label="Sampling Type">
          <b-dropdown
            v-model="selectedSamplingType"
            aria-role="list"
            :class="[$style.customSamplingDropdown, 'simple-dropdown']"
          >
            <button
              class="button simple-dropdown-button"
              slot="trigger"
              slot-scope="{ active }"
            >
              <!-- Audit SamplingType Selected Option -->
              <span>
                {{ auditSamplingTypeDisplayOptions[selectedSamplingType] }}
              </span>
              <b-icon :icon="active ? 'menu-up' : 'menu-down'"></b-icon>
            </button>
            <!-- Audit SamplingType Options -->
            <b-dropdown-item
              :value="value"
              v-for="value in AuditSamplingType"
              :key="value"
              aria-role="listitem"
              class="simple-dropdown-item"
            >
              {{ auditSamplingTypeDisplayOptions[value] }}
            </b-dropdown-item>
          </b-dropdown>
        </b-field>
      </div>

      <!-- Select Data Collection Period Details for One-Off or Ongoing RecurrenceType -->
      <div
        v-if="recurrenceSetting.recurrenceType != RecurrenceType.MULTIPLE"
        class="column is-full"
      >
        <AuditCycleDataSetup
          :key="`audit_cycle_${dataCollectionPeriodKey}_${0}`"
          :isCreateProject="true"
          :projectCriteria="projectCriteria"
          :projectSites="projectSites"
          :projectDataCollectors="projectDataCollectors"
          :selectedCriteria="getSelectedAuditCriteriaIds(0)"
          :selectedSites="getSelectedAuditSiteIds(0)"
          :selectedDataCollectors="getSelectedAuditParticipantsIds(0)"
          :recurrence="recurrenceSetting.recurrenceType"
          @update="(data) => handleUpdatedCycleData(data, 0)"
        />
      </div>
    </div>

    <!-- Option to Schedule Data Collection Period(s) for RecurrenceType MULTIPLE  -->
    <div v-if="recurrenceSetting.recurrenceType === RecurrenceType.MULTIPLE">
      <hr />
      <label><b>Schedule Data Collection Period</b></label>
      <br />
      <br />
      <div class="columns is-multiline">
        <!-- Select duration of recurrence cycles(Data Collection Period/Audit) -->
        <div class="column is-3">
          <b-field label="Every">
            <b-numberinput
              placeholder="Number"
              v-model="interval"
              @input="updateTotalDataCollectionPeriod"
              :controls="false"
            >
            </b-numberinput>
          </b-field>
          <!-- Error msg for validating input duration of recurrence cycles(Data Collection Period/Audit) -->
          <p v-if="showIntervalError" :class="$style.validationErrorMessage">
            Value cannot be empty or less than 1
          </p>
        </div>

        <!-- Select Recurrence Cycle Frequency -->
        <div class="column is-3 no-label-cycle padding-left-none">
          <b-field label="Cycle">
            <b-dropdown
              v-model="multipleRecurrenceType"
              @input="updateTotalDataCollectionPeriod"
              aria-role="list"
              class="participant-rowField-container"
            >
              <button
                class="button is-primary role-select-button"
                slot="trigger"
              >
                <span class="text-input">
                  {{ multipleRecurrenceType }}
                </span>
                <b-icon type="is-black" icon="menu-down" size="is-small" />
              </button>

              <b-dropdown-item
                :value="value.id"
                v-for="value in multipleRecurrenceFrequencyOptions"
                :key="value.id"
                aria-role="listitem"
                class="multiple-recurrence-input text-size-body"
              >
                {{ value.name }}
              </b-dropdown-item>
            </b-dropdown>
          </b-field>
        </div>

        <!-- Select no of Recurrence Cycles(Data Collection Period/Audit) -->
        <div class="column is-6 no-label-cycle">
          <label class="label">Produce a total of</label>
          <div class="inline-input-container">
            <b-field>
              <b-numberinput
                v-model="auditNumber"
                @input="updateTotalDataCollectionPeriod"
                placeholder="Number"
                :controls="false"
                :class="
                  showTotalError
                    ? $style.auditNumberErrorStyle
                    : $style.auditNumberStyle
                "
              ></b-numberinput>
            </b-field>
            <span class="inline-input-text">Data Collection Period(s)</span>
          </div>
          <!-- Error msg for validating input number of Recurrence Cycles(Data Collection Period/Audit) -->
          <p v-if="showTotalError" :class="$style.validationErrorMessage">
            Value must be between 1 to 10
          </p>
        </div>
      </div>
      <div v-if="interval >= 1">
        <!-- Select details for each Cycle(Data Collection Period/Audit)  -->
        <div
          v-for="(
            dataCollectionPeriod, datepickerIndex
          ) in dataCollectionPeriods"
          :key="datepickerIndex"
          :value="dataCollectionPeriod"
          class="columns is-multiline"
        >
          <!-- Option to select Date Range for Cycle(Data Collection Period/Audit) -->
          <ProjectCreationMultipleDatePicker
            :key="dataCollectionPeriodKey"
            :datepickerItem="dataCollectionPeriod"
            :periodIndex="datepickerIndex"
            @updateDatePicker="updateDataCollectionPeriodInitialSettings"
          />

          <!-- 
          Option to select Cycle(Data Collection Period/Audit) setup data such as:
           1. Criteria
           2. Sites
           3. Data Collectors
          -->
          <AuditCycleDataSetup
            :key="`audit_cycle_${dataCollectionPeriodKey}_${datepickerIndex}`"
            :isCreateProject="true"
            :projectCriteria="projectCriteria"
            :projectSites="projectSites"
            :projectDataCollectors="projectDataCollectors"
            :selectedCriteria="getSelectedAuditCriteriaIds(datepickerIndex)"
            :selectedSites="getSelectedAuditSiteIds(datepickerIndex)"
            :selectedDataCollectors="
              getSelectedAuditParticipantsIds(datepickerIndex)
            "
            :recurrence="recurrenceSetting.recurrenceType"
            @update="(data) => handleUpdatedCycleData(data, datepickerIndex)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import {
  MultipleRecurrenceFrequencyOptions,
  RecurrenceSettingPayload,
  DataCollectionPeriodDTO,
  DataCollectionPeriodSettingsPayload,
} from '@/store/modules/projects/types/projects.types';
import { StringInputOption } from '@/jbi-shared/types/form.types';
import AuditCycleDataSetup from './AuditCycleDataSetup.vue';
import ProjectCreationMultipleDatePicker from './ProjectCreationMultipleDatepicker.vue';
import moment from 'moment';
import { getStartDateTime, getEndDateTime } from '../../../utils/date.util';
import { cloneData } from '@/utils/general.util';
import {
  AuditSamplingType,
  MultipleRecurrenceType,
  RecurrenceType,
} from '@/jbi-shared/types/project.types';
import { AuditSamplingTypeDisplayOptions } from '@/store/modules/audits/types/audits.types';
import { Action, State } from 'vuex-class';
const dirtyMonths = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

@Component({
  components: {
    ProjectCreationMultipleDatePicker,
    AuditCycleDataSetup,
  },
})
export default class RecurrenceSettings extends Vue {
  @Prop() public deactivate!: boolean;
  @Prop({ type: Array, default: () => [] })
  public projectCriteria!: Array<{
    title: string;
    uniqueCriteriaId: string | number;
  }>;
  @Prop({ type: Array, default: () => [] })
  public projectSites!: Array<{ id: number; name: string }>;
  @Prop({ type: Array, default: () => [] })
  public projectDataCollectors!: Array<{
    email: string;
    siteIds: number[];
  }>;

  @Action('projects/saveProjectSettingFormData')
  public saveProjectSettingFormData!: (
    payload: RecurrenceSettingPayload | undefined,
  ) => void;

  @State((state) => state.projects.projectSettingForm)
  public projectSettingForm!: RecurrenceSettingPayload;

  public minStartDate: Date = new Date(
    new Date().setDate(new Date().getDate() - 1),
  );
  public defaultEndDate: moment.Moment = moment(new Date()).add(1, 'M');
  public dataCollectionPeriodKey: number = Math.floor(Math.random() * 999);
  public showIntervalError: boolean = false; // Error msg for validating input duration of recurrence cycles(Data Collection Period/Audit)
  public showTotalError: boolean = false; // Error msg for validating input number of Recurrence Cycles(Data Collection Period/Audit)
  public isFormDataValid: boolean = false;

  get recurrenceSetting() {
    /** if projectSettingForm exists use projectSettingForm else set default values */
    if (this.projectSettingForm) {
      return this.projectSettingForm;
    } else {
      const recurrenceSetting: RecurrenceSettingPayload = {
        recurrenceType: RecurrenceType.ONE_OFF,
        multipleRecurrenceType: null,
        auditSamplingType: AuditSamplingType.TARGET_SAMPLE_SIZE,
        interval: 1,
        auditNumber: 1,
        scheduledRecurrenceStartDate: new Date(),
        scheduledRecurrenceEndDate: null,
        enableGrip: true,
        dataCollectionPeriodSettings: {
          0: {
            auditNumber: 1,
            auditName: 'Baseline',
            startDate: new Date(),
            endDate: this.defaultEndDate.subtract(1, 'd').toDate(),
            auditCriteriaIds: (this.projectCriteria || []).map(
              (pc) => pc.uniqueCriteriaId,
            ),
            auditSiteIds: (this.projectSites || []).map((ps) => ps.id),
            auditParticipants: cloneData(this.projectDataCollectors || []),
          },
        },
      };

      return recurrenceSetting;
    }
  }

  public emitChanges(payload: RecurrenceSettingPayload) {
    this.isFormDataValid = Object.keys(
      payload.dataCollectionPeriodSettings,
    ).every(
      (key: any) =>
        payload.dataCollectionPeriodSettings[key].auditCriteriaIds.length > 0 &&
        payload.dataCollectionPeriodSettings[key].auditSiteIds.length > 0,
    );
    this.$emit('updateRecurrenceSetting', payload);
    this.saveProjectSettingFormData(payload);
    this.$emit('updateIsFormDataValid', this.isFormDataValid);
  }

  syncData() {
    const settings: RecurrenceSettingPayload = cloneData(
      this.projectSettingForm,
    );

    const projectCriteriaIds = (this.projectCriteria || []).map(
      (pc) => pc.uniqueCriteriaId,
    );
    const projectSitesIds = (this.projectSites || []).map((ps) => ps.id);
    const dataCollectionPeriods: DataCollectionPeriodSettingsPayload = {};

    Object.keys(settings.dataCollectionPeriodSettings).map((key: any) => {
      const tempSettings: DataCollectionPeriodDTO = cloneData(
        settings.dataCollectionPeriodSettings[key],
      );

      const auditCriteriaIds = tempSettings.auditCriteriaIds.filter((ac: any) =>
        projectCriteriaIds.includes(ac),
      );

      const auditSiteIds = tempSettings.auditSiteIds.filter((as) =>
        projectSitesIds.includes(as),
      );
      const auditParticipants: {
        email: string;
        siteIds: number[];
      }[] = [];
      tempSettings.auditParticipants.forEach((ap) => {
        const user = (this.projectDataCollectors || []).find(
          (pdc) => pdc.email === ap.email,
        );

        if (user) {
          const sites = user.siteIds.filter((siteId) =>
            tempSettings.auditSiteIds.includes(siteId),
          );

          if (sites.length > 0) {
            auditParticipants.push({ ...user, siteIds: sites });
          }
        }
      });

      const data: DataCollectionPeriodDTO = {
        auditNumber: tempSettings.auditNumber,
        auditName: tempSettings.auditName,
        startDate: tempSettings.startDate,
        endDate: tempSettings.endDate,
        auditSiteIds,
        auditCriteriaIds,
        auditParticipants,
      };

      dataCollectionPeriods[key] = data;
    });

    this.saveProjectSettingFormData({
      ...settings,
      dataCollectionPeriodSettings: dataCollectionPeriods,
    });
  }

  public getSelectedAuditCriteriaIds(index: number): Array<string | number> {
    if (this.recurrenceSetting.dataCollectionPeriodSettings[index]) {
      return this.recurrenceSetting.dataCollectionPeriodSettings[index]
        .auditCriteriaIds;
    }
    return [];
  }

  public getSelectedAuditSiteIds(index: number): number[] {
    if (this.recurrenceSetting.dataCollectionPeriodSettings[index]) {
      return this.recurrenceSetting.dataCollectionPeriodSettings[index]
        .auditSiteIds;
    }
    return [];
  }

  public getSelectedAuditParticipantsIds(index: number): string[] {
    if (this.recurrenceSetting.dataCollectionPeriodSettings[index]) {
      return this.recurrenceSetting.dataCollectionPeriodSettings[
        index
      ].auditParticipants.map((ap) => ap.email);
    }

    return [];
  }

  get RecurrenceType(): typeof RecurrenceType {
    return RecurrenceType;
  }

  get AuditSamplingType(): typeof AuditSamplingType {
    return AuditSamplingType;
  }

  get auditSamplingTypeDisplayOptions() {
    return AuditSamplingTypeDisplayOptions;
  }

  get selectedSamplingType(): AuditSamplingType {
    return this.recurrenceSetting.auditSamplingType;
  }

  set selectedSamplingType(newValue) {
    const temp = cloneData(this.recurrenceSetting);
    temp.auditSamplingType = newValue;
    this.dataCollectionPeriodKey += 1;
    this.emitChanges(temp);
  }

  get selectedRecurrenceType(): RecurrenceType {
    return this.recurrenceSetting.recurrenceType;
  }

  set selectedRecurrenceType(newValue) {
    const temp = cloneData(this.recurrenceSetting);
    temp.recurrenceType = newValue;
    temp.auditNumber = 1;
    temp.interval = 1;
    temp.multipleRecurrenceType =
      newValue === RecurrenceType.MULTIPLE
        ? MultipleRecurrenceType.MONTHLY
        : null;

    if (newValue === RecurrenceType.MULTIPLE) {
      this.updateTotalDataCollectionPeriod();
    }
    this.emitChanges(temp);
  }

  get scheduledRecurrenceStartDate() {
    const recurrenceStartDate: any = this.recurrenceSetting
      .scheduledRecurrenceStartDate;
    return recurrenceStartDate !== undefined && recurrenceStartDate
      ? getStartDateTime(recurrenceStartDate)
      : getStartDateTime();
  }

  set scheduledRecurrenceStartDate(newValue) {
    const temp = cloneData(this.recurrenceSetting);
    temp.scheduledRecurrenceStartDate = newValue;
    this.dataCollectionPeriodKey += 1;
    this.emitChanges(temp);
  }

  get dataCollectionPeriods() {
    return this.recurrenceSetting.dataCollectionPeriodSettings
      ? this.recurrenceSetting.dataCollectionPeriodSettings
      : this.updateTotalDataCollectionPeriod();
  }

  get multipleRecurrenceFrequencyOptions(): StringInputOption[] {
    return Object.entries(MultipleRecurrenceFrequencyOptions).map(
      ([key, value]) => {
        return {
          id: key,
          name: value,
        };
      },
    );
  }

  get multipleRecurrenceType(): MultipleRecurrenceType {
    return this.recurrenceSetting.multipleRecurrenceType !== undefined &&
      this.recurrenceSetting.multipleRecurrenceType
      ? this.recurrenceSetting.multipleRecurrenceType
      : MultipleRecurrenceType.MONTHLY;
  }

  set multipleRecurrenceType(newValue) {
    const temp = cloneData(this.recurrenceSetting);
    temp.multipleRecurrenceType = newValue;
    this.dataCollectionPeriodKey += 1;
    this.emitChanges(temp);
  }

  get interval(): number {
    return this.recurrenceSetting.interval
      ? this.recurrenceSetting.interval
      : 0;
  }

  set interval(newValue: number) {
    const temp = cloneData(this.recurrenceSetting);
    temp.interval = newValue;
    this.showIntervalError = !newValue || newValue < 1 ? true : false;
    this.dataCollectionPeriodKey += 1;
    this.emitChanges(temp);
  }

  get auditNumber(): number {
    return this.recurrenceSetting.auditNumber
      ? this.recurrenceSetting.auditNumber
      : 0;
  }

  set auditNumber(newValue: number) {
    const temp = cloneData(this.recurrenceSetting);
    temp.auditNumber = newValue;
    this.showTotalError = newValue < 1 || newValue > 10;
    this.dataCollectionPeriodKey += 1;
    this.emitChanges(temp);
  }

  /** Returns title and description for each recurrence type, used for display purposes  */
  public recurrenceTypeDisplayInfo(
    type: RecurrenceType,
  ): {
    title: string;
    infotext1: string;
    infotext2: string | undefined;
  } {
    switch (type) {
      case RecurrenceType.ONE_OFF:
        return {
          title: 'One-off',
          infotext1:
            'Create a one off project for a specified data collection period.',
          infotext2:
            '(You can add subsequent data collection periods at a later date if required)',
        };
      case RecurrenceType.ONGOING:
        return {
          title: 'Ongoing',
          infotext1:
            'Create an ongoing project where data collection happens continuously over weeks, months or years.',
          infotext2: undefined,
        };
      case RecurrenceType.MULTIPLE:
        return {
          title: 'Multiple Data Collection Periods',
          infotext1:
            'Create multiple data collection periods such as for a pre-test post-test study, as part of an audit and feedback (and audit) project, or quality improvement cycles.',
          infotext2: undefined,
        };
      default:
        return {
          title: '',
          infotext1: '',
          infotext2: '',
        };
    }
  }

  /**
   * Handles assigning auditCriteriaIds, auditSiteIds, and auditParticipants to the selected index,
   * then emits the updated data to the parent component to reflect the changes
   */
  public handleUpdatedCycleData(
    data: {
      auditCriteriaIds: (string | number)[];
      auditSiteIds: number[];
      auditParticipants: { email: string; siteIds: number[] }[];
    },
    index: number,
  ) {
    const temp = cloneData(this.recurrenceSetting);

    // Store the selected data in the recurrence settings
    temp.dataCollectionPeriodSettings[index].auditCriteriaIds =
      data.auditCriteriaIds;
    temp.dataCollectionPeriodSettings[index].auditSiteIds = data.auditSiteIds;
    temp.dataCollectionPeriodSettings[index].auditParticipants =
      data.auditParticipants;

    // Emit update to parent
    this.emitChanges(temp);
  }

  public getDate(newValue: Date): string {
    return (
      newValue.getDate() +
      ' ' +
      dirtyMonths[newValue.getMonth()] +
      ' ' +
      newValue.getFullYear()
    );
  }

  public formatDateRange(date: Date): string {
    return this.getDate(date);
  }

  public updateDataCollectionPeriodInitialSettings(newValue: {
    auditName: string;
    auditNumber: number;
    startDate: Date;
    endDate: Date;
  }) {
    const tempUpdatedSettings = {
      ...this.recurrenceSetting,
    };

    tempUpdatedSettings.dataCollectionPeriodSettings[newValue.auditNumber] = {
      ...tempUpdatedSettings.dataCollectionPeriodSettings[newValue.auditNumber],
      ...newValue,
    };

    this.emitChanges(tempUpdatedSettings);
  }

  /**
   * Updates initial settings for the selectedRecurrenceType 'MULTIPLE'.
   *
   * When the user selects the number of recurrence cycles,
   * this function generates and initializes the default data structure for each cycle.
   */
  public updateTotalDataCollectionPeriod() {
    const dataCollectionPeriodSettings = [];
    const scheduledStartDate = moment(this.scheduledRecurrenceStartDate);
    if (this.selectedRecurrenceType !== RecurrenceType.MULTIPLE) {
      dataCollectionPeriodSettings.push({
        startDate: getStartDateTime(scheduledStartDate),
        endDate: null,
        auditNumber: 1,
        auditName: 'Baseline',
        auditCriteriaIds: (this.projectCriteria || []).map(
          (pc) => pc.uniqueCriteriaId,
        ),
        auditSiteIds: (this.projectSites || []).map((ps) => ps.id),
        auditParticipants: cloneData(this.projectDataCollectors || []),
      });
    } else {
      const intervalValue = this.interval;
      let auditNumberValue = this.auditNumber;

      /** Reset auditNumberValue to 1 if it exceeds 10, and exit early to prevent further processing */
      if (auditNumberValue > 10) {
        auditNumberValue = 1;
        return;
      }

      for (let value = 0; value < auditNumberValue; value++) {
        const startDate =
          value > 0
            ? scheduledStartDate.add(1, 'd').toDate()
            : scheduledStartDate.toDate();

        const newAuditName = value > 0 ? `Follow Up ${value}` : 'Baseline';
        switch (this.multipleRecurrenceType) {
          case Object.keys(MultipleRecurrenceFrequencyOptions)[0]:
            scheduledStartDate.add(intervalValue, 'd');
            break;
          case Object.keys(MultipleRecurrenceFrequencyOptions)[1]:
            scheduledStartDate.add(intervalValue, 'w');
            break;
          case Object.keys(MultipleRecurrenceFrequencyOptions)[2]:
            scheduledStartDate.add(intervalValue, 'M');
            break;
          default:
            scheduledStartDate.add(intervalValue, 'y');
            break;
        }
        const newEndDate: Date = scheduledStartDate.subtract(1, 'd').toDate();
        const dateRangePeriod: Date[] = [startDate, newEndDate];
        dataCollectionPeriodSettings.push({
          startDate: getStartDateTime(dateRangePeriod[0]),
          endDate: getEndDateTime(dateRangePeriod[1]),
          auditNumber: value + 1,
          auditName: newAuditName,
          auditCriteriaIds: (this.projectCriteria || []).map(
            (pc) => pc.uniqueCriteriaId,
          ),
          auditSiteIds: (this.projectSites || []).map((ps) => ps.id),
          auditParticipants: cloneData(this.projectDataCollectors || []),
        });
      }
    }

    const multipleAuditCyclesData: DataCollectionPeriodSettingsPayload = {
      ...dataCollectionPeriodSettings,
    };
    const temp = cloneData(this.recurrenceSetting);
    temp.dataCollectionPeriodSettings = multipleAuditCyclesData;
    this.emitChanges(temp);
    return multipleAuditCyclesData;
  }
}
</script>
<style lang="scss" module>
.radioBtn label {
  background-color: #ffffff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  height: 250px;
  color: #444f51;
  font-size: 16px;
  font-weight: 400 !important;
  padding-right: 5px;
  margin-right: 0;
}

.activeRadioBtn label {
  color: #fff !important;
  font-size: 16px;
  font-weight: 400 !important;
  background-color: #21b5ea !important;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  height: 250px;
  padding-right: 5px;
  margin-right: 0;
}
.header,
.text {
  display: block;
  height: 100%;
  max-width: 100%;
  text-overflow: clip;
  white-space: normal;
  text-align: start;
  font-size: 14px;
  line-height: 17px;
  color: #444f51;
  padding: 0.7rem 0.75rem 1.5rem 0.75rem;
  flex-basis: 100%;
}
.activeHeader {
  color: #fff;
  display: block;
  height: 100%;
  max-width: 100%;
  text-overflow: clip;
  white-space: normal;
  text-align: start;
  font-size: 14px;
  line-height: 17px;
  padding: 0.7rem 0.75rem 1.5rem 0.75rem;
  flex-basis: 100%;
}
.radioBtnContainer {
  margin-bottom: 40px;
}
.extraData {
  color: #9a9a9a;
  font-size: 12px;
  line-height: 14px;
  font-weight: 400;
  text-align: left;
}
.auditNumberInput::-webkit-inner-spin-button,
.auditNumberInput::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.auditNumberStyle {
  input {
    border: 0 solid;
    background-color: transparent;
  }
}
.auditNumberErrorStyle {
  input {
    border: 1px solid #f14668;
  }
}
.customCalendarIcon {
  i::before {
    padding-top: 13px;
    padding-bottom: 12px;
    width: 15px;
    margin-right: 10px;
  }
}
.period {
  label {
    text-align: left;
  }
}
.inputContainer {
  position: relative;
  float: right;
  input {
    width: 80px;
    height: 44px;
    text-indent: 15px;
    background-color: white !important;
    border-color: #dfdfdf !important;
    color: #444f51 !important;
    padding: 7px 7px;
    border-top-right-radius: 4px !important;
    border-bottom-right-radius: 4px !important;
  }
  .percentageContainer {
    position: absolute;
    right: 6px;
    font-size: 18px;
    top: 13px;
    z-index: 4;
  }
}
.validationErrorMessage {
  color: red;
  font-size: 12px;
  margin-top: 4px;
}
.customSamplingDropdown {
  // width: 500px;
}

.disabled {
  pointer-events: none;
  opacity: 0.7;
}

.cycleLabel {
  text-align: left;
  font-weight: 600;
  font-size: 16px;
  color: #444f51;
  padding-bottom: 1rem;
}
</style>
